import { useEffect, useCallback, useMemo } from 'react';
import { useDataModel } from '@thd-nucleus/data-sources';
import { triggerNewRelic, mkTelemetryField, navigate } from '../utils/helpers';
import { iframeAnalytics } from '../utils/analytics';
import { newRelicConstants } from '../core/constants';
import { useClickInteractionListener } from '@thd-olt-functional/utils';

export const useRecsCarouselPodTelemetry = (props) => {
  const {
    productPodNode,
    parent,
    product: productData,
    itemId,
    storeId,
    position,
    url: urlProp,
    title,
    podKpiName = newRelicConstants.RECS_CONTAINER,
    anchorProduct,
    strategy,
    apiName,
    isIframe = false,
    // noMountKpi: when true, the mount KPI will not be triggered. This is useful for cases where the KPI
    // is triggered in a different component to avoid duplicate KPIs.
    noMountKpi = false,
    // noRightClickTracker: when true, the useClickInteractionListener will not be set up to track right-clicks.
    noRightClickTracker = false
  } = props;

  // --
  const { data } = useDataModel('product', {
    variables: {
      itemId,
      storeId
    },
    // Note: skip query if itemId is not available or if productData is already provided via props, to avoid unnecessary query execution.
    skip: !itemId || Boolean(productData)
  });
  const product = productData || data?.product;

  // --
  const url = urlProp || product?.identifiers?.canonicalUrl;
  const { isSponsored, sponsoredBeacon, sponsoredMetadata } = product?.info || {};
  const { trackSource, slotId, sponsoredId } = sponsoredMetadata || {};
  const sponsoredInfo = isSponsored ? { isSponsored, sponsoredBeacon, sponsoredMetadata } : null;

  // --
  const baseTelemetryFields = useMemo(() => {
    // ---
    const fieldItemId = mkTelemetryField({ key: 'itemId', value: itemId });
    const fieldSponsoredId = mkTelemetryField({ key: 'sponsoredId', value: sponsoredId });
    const fieldTrackSource = mkTelemetryField({ key: 'trackSource', value: trackSource });
    const fieldPosition = mkTelemetryField({ key: 'position', value: position });
    const fieldSlotId = mkTelemetryField({ key: 'slotId', value: slotId });
    const fieldTitle = mkTelemetryField({ key: 'title', value: title?.toLowerCase()?.replace(/\s/g, '') });
    const fieldParent = mkTelemetryField({ key: 'parent', value: parent });
    // ---

    return `${fieldItemId}-${fieldSponsoredId}-${fieldTrackSource}-${fieldPosition}-${fieldSlotId}-${fieldTitle}-${fieldParent}`;

  }, [itemId, sponsoredId, trackSource, position, slotId, title, parent]);

  /**
     * PLA_PRODUCT_POD_MOUNT - NewRelic.
     */
  useEffect(() => {
    // check
    if (noMountKpi || !isSponsored || !productPodNode) {
      return;
    }

    const kpiName = `${podKpiName}-${baseTelemetryFields}`;
    const kpiValue = newRelicConstants.PLA_PRODUCT_POD_MOUNT;

    triggerNewRelic(kpiName, kpiValue);

  }, [noMountKpi, isSponsored, productPodNode, podKpiName, baseTelemetryFields]);

  // ---- ----
  // Click event triggers
  // ---- ----

  /**
     * 'CLICK' - Base handler to trigger click to NewRelic.
     *
     * @param src should be one of:
     * - productShowNowButton
     * - productImage
     * - productHeader
     * - productRating
     * - productPod
     * - clickNewTab
     */
  const triggerNewRelicClick = useCallback((src) => {
    const srcField = mkTelemetryField({ key: 'src', value: src });
    const telemetryFields = `${baseTelemetryFields}-${srcField}`;

    const kpiName = `${podKpiName}-${telemetryFields}`;
    const kpiValue = newRelicConstants.CLICK;

    triggerNewRelic(kpiName, kpiValue);
  }, [baseTelemetryFields, podKpiName]);

  /**
     * Triggers 'product-pod-v7.click' event - Analytics & AdServer.
     *
     * This event ensures:
     *    - Analytics capture the click event.
     *    - AdServer onClickBeacon is fired.
     */
  const triggerProductPodV7Click = useCallback(() => {

    const productPodEventData = {
      podAction: 'product pod',
      podAnchorSku: itemId,
      target: '_self',
      parent,
    };
    window.LIFE_CYCLE_EVENT_BUS.trigger('product-pod-v7.click', productPodEventData);
  }, [itemId, parent]);

  /**
     * Triggers 'cartItem.DD_EVENT' event - Analytics & AdServer.
     *
     * This event ensures:
     *    - Analytics capture the given event (i.e. click/atc).
     *    - AdServer click-beacon is fired when the product is sponsored.
     *
     * This event is only triggered when the component is rendered inside
     * an iframe (e.g. ATC drawer) as per requirements.
     */
  const triggerIframeAnalytics = useCallback((eventName) => {
    if (!isIframe) {
      return;
    }

    const iframeAnalyticsObject = {
      anchorProduct,
      itemId,
      position,
      apiName,
      strategy,
      sponsoredInfo
    };

    if (typeof window?.top?.LIFE_CYCLE_EVENT_BUS?.trigger === 'function') {
      window?.top?.LIFE_CYCLE_EVENT_BUS?.trigger('cartItem.DD_EVENT', iframeAnalytics(iframeAnalyticsObject, eventName));
    }
  }, [isIframe, anchorProduct, itemId, position, apiName, strategy, sponsoredInfo]);
  // --- ---

  // ---- ----
  // Click event handlers
  // ---- ----

  // Utility function to handle modifier (cmd/ctrl/shift) keys check
  const modifierClick = (event) => {
    return (event.metaKey || event.ctrlKey || event.shiftKey);
  };

  /**
   * Click handler for sponsored case.
   */
  const handleClickOnSponsored = useCallback((event) => {
    if (!isSponsored) {
      return;
    }

    // Case: sponsored.
    // If modifier keys are pressed, allow default behavior (i.e open in new tab) without triggering analytics or navigation.
    // The click event will be handled later by the useClickInteractionListener hook.

    if (modifierClick(event)) {
      return;
    }

    // --
    event.preventDefault();

    isIframe ? triggerIframeAnalytics('click') : triggerProductPodV7Click(); // analytic_&_ad-server
    triggerNewRelicClick(newRelicConstants.CLICK_SRC_PRODUCT_POD); // new-relic
    navigate(url); // redirect

  }, [isSponsored, isIframe, url, triggerProductPodV7Click, triggerIframeAnalytics, triggerNewRelicClick]);


  /**
   * Click handler for non-sponsored case.
   */
  const handleClickOnNonSponsored = useCallback((event) => {
    if (isSponsored) {
      return;
    }

    // Case: non-sponsored pod.
    // Allow default behavior (open in new tab) if modifier keys are pressed and the target is a link,
    // but still trigger analytics.

    if (modifierClick(event) && event.target?.closest?.('a')) {
      isIframe ? triggerIframeAnalytics('click') : triggerProductPodV7Click(); // analytic

    } else {
      event.preventDefault();

      isIframe ? triggerIframeAnalytics('click') : triggerProductPodV7Click(); // analytic
      navigate(url); // redirect
    }

  }, [isSponsored, isIframe, url, triggerProductPodV7Click, triggerIframeAnalytics]);

  /**
   * Handler on: pod 'click'
   *
   * IMPORTANT:
   * ----------
   * This handler is meant to be attached to any area (e.g. link element covering entire pod, or pod-tapper component) not covered
   * by product-pod subcomponents (e.g. image, header, rating, ATC button, which already have their own dedicated click handlers
   * for analytics and ad-server tracking).
   */
  const handleClick = useCallback((event) => {
    if (!isSponsored) {
      handleClickOnNonSponsored(event);
    } else {
      handleClickOnSponsored(event);
    }
  }, [isSponsored, handleClickOnNonSponsored, handleClickOnSponsored]);

  /**
   * Handler on: ATC 'click'
   */
  const handleClickOnProductAtcButton = useCallback(() => {
    // Case: is iframe.
    // -----
    // The current AddToCart component used for the iframe scenario (e.g. VerticalRecsDrawer) currently doesn't
    // triggers the 'cartItem.DD_EVENT' event on its own, so we need to trigger it manually here to ensure the
    // analytics and ad-server tracking is working as expected.
    if (isIframe) {

      // analytic (always) & ad-server (when is sponsored)
      triggerIframeAnalytics('atc');
    }

    // Case: is sponsored.
    // -----
    if (isSponsored) {
      triggerNewRelicClick(newRelicConstants.CLICK_SRC_PRODUCT_ATC_BUTTON); // new-relic
    }
  }, [isIframe, isSponsored, triggerIframeAnalytics, triggerNewRelicClick]);

  /**
   * Handler on: image 'click'
   */
  const handleClickOnProductImage = useCallback((event) => {
    // --
    // Case: non-sponsored. The subcomponent already does the required click handling.
    if (!isSponsored) return;

    // --
    // Case: new-tab. This scenario is handled in the useClickInteractionListener.
    if (modifierClick(event)) return;

    // --
    // Case: sponsored. Adds the new-relic call (in addition to analytic & ad-server tracking implemented in the subcomponent).
    event.preventDefault();
    triggerNewRelicClick(newRelicConstants.CLICK_SRC_PRODUCT_IMAGE); // new-relic
    navigate(url); // redirect

  }, [isSponsored, url, triggerNewRelicClick]);

  /**
   * Handler on: header 'click'
   */
  const handleClickOnProductHeader = useCallback((event) => {
    // --
    // Case: non-sponsored. The subcomponent already does the required click handling.
    if (!isSponsored) return;

    // --
    // Case: new-tab. This scenario is handled in the useClickInteractionListener.
    if (modifierClick(event)) return;

    // --
    // Case: sponsored. Adds the new-relic call (in addition to analytic & ad-server tracking implemented in the subcomponent).
    event.preventDefault();
    triggerNewRelicClick(newRelicConstants.CLICK_SRC_PRODUCT_HEADER); // new-relic
    navigate(url); // redirect

  }, [isSponsored, url, triggerNewRelicClick]);

  /**
   * Handler on: rating 'click'
   */
  const handleClickOnProductRating = useCallback((event) => {
    // --
    // Case: non-sponsored. The subcomponent already does the required click handling.
    if (!isSponsored) return;

    // --
    // Case: new-tab. This scenario is handled in the useClickInteractionListener.
    if (modifierClick(event)) return;

    // --
    // Case: sponsored. Adds the new-relic call (in addition to analytic & ad-server tracking implemented in the subcomponent).
    event.preventDefault();
    triggerNewRelicClick(newRelicConstants.CLICK_SRC_PRODUCT_RATING); // new-relic
    navigate(url); // redirect

  }, [isSponsored, url, triggerNewRelicClick]);

  /**
   * Trigger click event to all targets: Analytics, AdServer & NewRelic.
   */
  const sendAnalytics = useCallback(() => {
    // --
    isIframe ? triggerIframeAnalytics('click') : triggerProductPodV7Click(); // analytic_&_ad-server
    triggerNewRelicClick(newRelicConstants.CLICK_SRC_NEW_TAB); // new-relic
  }, [isIframe, triggerProductPodV7Click, triggerIframeAnalytics, triggerNewRelicClick]);

  // -----------------
  // New-Tab Tracker
  // -----------------
  useClickInteractionListener({
    callbackFn: sendAnalytics,
    targetNode: (noRightClickTracker || !isSponsored) ? (null) : (productPodNode),
    itemId,
    sponsoredId,
    trackSource,
    slotId,
    position
  });

  return {
    isSponsored,
    url,
    triggerProductPodV7Click,
    handleClick,
    handleClickOnProductAtcButton,
    handleClickOnProductImage,
    handleClickOnProductHeader,
    handleClickOnProductRating,
    sendAnalytics
  };
};
